/**
 * Properties Store - Zustand state management for properties
 */

import { create } from 'zustand';
import type { Property, PropertyWithStats } from '@raffle/types';
import { api } from '../lib/api';

interface PropertiesState {
  properties: Property[];
  currentProperty: PropertyWithStats | null;
  isLoading: boolean;
  error: string | null;
  totalPages: number;
  currentPage: number;
  
  // Actions
  fetchProperties: (params?: { page?: number; status?: string; search?: string }) => Promise<void>;
  fetchProperty: (id: string) => Promise<void>;
  clearError: () => void;
}

export const usePropertiesStore = create<PropertiesState>((set) => ({
  properties: [],
  currentProperty: null,
  isLoading: false,
  error: null,
  totalPages: 1,
  currentPage: 1,

  fetchProperties: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const result = await api.properties.list({
        page: params.page || 1,
        pageSize: 12,
        status: params.status,
        search: params.search,
      });

      if (result.success && result.data) {
        set({
          properties: result.data.items,
          totalPages: result.data.meta.totalPages,
          currentPage: result.data.meta.page,
          isLoading: false,
        });
      } else {
        set({ error: result.error?.message || 'Failed to fetch properties', isLoading: false });
      }
    } catch (error) {
      set({ error: 'Failed to fetch properties', isLoading: false });
    }
  },

  fetchProperty: async (id: string) => {
    set({ isLoading: true, error: null, currentProperty: null });
    try {
      const result = await api.properties.get(id);

      if (result.success && result.data) {
        set({ currentProperty: result.data, isLoading: false });
      } else {
        set({ error: result.error?.message || 'Property not found', isLoading: false });
      }
    } catch (error) {
      set({ error: 'Failed to fetch property', isLoading: false });
    }
  },

  clearError: () => set({ error: null }),
}));
